/**
 * VirtualCardsTab - Tab content for virtual cards management
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { 
  CreditCard, 
  Eye, 
  EyeOff, 
  Snowflake, 
  Play, 
  Trash2, 
  Loader2,
  Wifi,
  TrendingUp,
  MoreHorizontal,
  Sparkles,
  Settings2,
  Brain,
  Download,
  FileText,
  Clock,
  AlertTriangle,
  AlertCircle,
  Info
} from 'lucide-react';
import { motion } from 'framer-motion';
import { CATEGORY_COLORS, getCategoryIcon } from './constants';

const VirtualCardsTab = ({
  cards,
  selectedCard,
  setSelectedCard,
  showDetails,
  toggleShowDetails,
  handleToggleStatus,
  handleDeleteCard,
  handleExportCSV,
  handleExportPDF,
  handleSimulateSpending,
  handleGetAIRecommendations,
  setBudgetDialogOpen,
  simulatingSpending,
  loadingAI,
  exporting,
  cardAnalytics,
  cardTransactions,
  cardAlerts,
  analyticsLoading,
  alertThreshold,
  budgetStatus
}) => {
  const formatCardNumber = (number, show) => {
    if (show) return number.replace(/(.{4})/g, '$1 ').trim();
    return `•••• •••• •••• ${number.slice(-4)}`;
  };

  const getCardBackground = (type, index) => {
    const backgrounds = [
      'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
      'bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900',
      'bg-gradient-to-br from-emerald-900 via-teal-800 to-cyan-900',
    ];
    return type === 'premium' 
      ? 'bg-gradient-to-br from-amber-600 via-yellow-500 to-amber-600' 
      : backgrounds[index % backgrounds.length];
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      case 'warning': return <AlertCircle className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'critical': return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'warning': return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
      default: return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {cards.map((card, index) => (
          <motion.div 
            key={card.id} 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: index * 0.1 }}
          >
            <Card 
              className={`overflow-hidden cursor-pointer transition-all ${
                selectedCard?.id === card.id ? 'ring-2 ring-emerald-500' : ''
              }`} 
              onClick={() => setSelectedCard(card)}
            >
              <CardContent className="p-0">
                <div className={`relative p-6 ${getCardBackground(card.card_type, index)} text-white aspect-[1.586] max-h-52 virtual-card`}>
                  <div className="virtual-card-shine" />
                  <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <Wifi className="w-5 h-5 rotate-90" />
                        {card.status === 'frozen' && (
                          <Badge variant="secondary" className="bg-white/20 text-white text-xs">
                            <Snowflake className="w-3 h-3 mr-1" />Frozen
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs opacity-70">Virtual</p>
                        <p className="font-bold text-sm">{card.card_type === 'premium' ? 'GOLD' : 'DEBIT'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="font-mono text-lg tracking-wider mb-3">
                        {formatCardNumber(card.card_number, showDetails[card.id])}
                      </p>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-xs opacity-70">VALID THRU</p>
                          <p className="font-mono text-sm">{card.expiry_date}</p>
                        </div>
                        <div>
                          <p className="text-xs opacity-70">CVV</p>
                          <p className="font-mono text-sm">{showDetails[card.id] ? card.cvv : '•••'}</p>
                        </div>
                        <div className="flex gap-1">
                          <div className="w-6 h-6 rounded-full bg-red-500 opacity-80" />
                          <div className="w-6 h-6 rounded-full bg-orange-400 opacity-80 -ml-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Monthly Limit</p>
                      <p className="font-mono font-semibold">${card.spending_limit.toFixed(0)}</p>
                    </div>
                    <Badge variant={card.status === 'active' ? 'default' : 'secondary'}>
                      {card.status}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 rounded-lg" 
                      onClick={(e) => { e.stopPropagation(); toggleShowDetails(card.id); }}
                    >
                      {showDetails[card.id] ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {showDetails[card.id] ? 'Hide' : 'Show'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 rounded-lg" 
                      onClick={(e) => { e.stopPropagation(); handleToggleStatus(card.id, card.status); }}
                    >
                      {card.status === 'active' ? <Snowflake className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                      {card.status === 'active' ? 'Freeze' : 'Unfreeze'}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="rounded-lg" 
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleExportCSV(card.id)}>
                          <Download className="w-4 h-4 mr-2" />Export CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExportPDF(card.id)}>
                          <FileText className="w-4 h-4 mr-2" />Export Report
                        </DropdownMenuItem>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Card?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently delete the card.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteCard(card.id)} 
                                className="bg-destructive text-destructive-foreground"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Selected Card Details */}
      {selectedCard && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-xl font-heading font-semibold">
              Card •••• {selectedCard.card_number.slice(-4)} Details
            </h2>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleGetAIRecommendations} 
                disabled={loadingAI} 
                data-testid="ai-recommendations-btn"
              >
                {loadingAI ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                AI Recommendations
              </Button>
              <Button variant="outline" size="sm" onClick={() => setBudgetDialogOpen(true)}>
                <Settings2 className="w-4 h-4 mr-2" />Budgets
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleSimulateSpending(selectedCard.id)} 
                disabled={simulatingSpending || selectedCard.status === 'frozen'}
              >
                {simulatingSpending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Simulate
              </Button>
            </div>
          </div>

          {/* Card Alerts */}
          {cardAlerts?.alerts?.length > 0 && (
            <div className="space-y-2">
              {cardAlerts.alerts.map((alert, idx) => (
                <div key={`alert-${idx}`} className={`flex items-center gap-3 p-3 rounded-xl border ${getAlertColor(alert.type)}`}>
                  {getAlertIcon(alert.type)}
                  <div className="flex-1">
                    <p className="font-medium text-sm">{alert.title}</p>
                    <p className="text-xs opacity-80">{alert.message}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold">${alert.spent.toFixed(2)}</p>
                    <p className="text-xs opacity-70">of ${alert.budget.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {analyticsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : cardAnalytics ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Spent</p>
                        <p className="text-2xl font-heading font-bold">${cardAnalytics.total_spent.toFixed(2)}</p>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                      </div>
                    </div>
                    <Progress value={cardAnalytics.limit_used_percent} className="mt-3 h-2" />
                    <p className="text-xs text-muted-foreground mt-1">{cardAnalytics.limit_used_percent}% of limit</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Transactions</p>
                    <p className="text-2xl font-heading font-bold">{cardAnalytics.transaction_count}</p>
                    <p className="text-xs text-muted-foreground mt-1">All time</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Avg. Transaction</p>
                    <p className="text-2xl font-heading font-bold">${cardAnalytics.average_transaction.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Per purchase</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Last 7 Days</p>
                    <p className="text-2xl font-heading font-bold">${cardAnalytics.recent_activity?.total?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-muted-foreground mt-1">{cardAnalytics.recent_activity?.count || 0} txns</p>
                  </CardContent>
                </Card>
              </div>

              {/* Budget Status with Periods */}
              {budgetStatus?.budget_status?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5" /> Budget Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {budgetStatus.budget_status.map((status) => (
                        <div key={status.category} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const CategoryIcon = getCategoryIcon(status.category);
                                return (
                                  <div 
                                    className="w-6 h-6 rounded flex items-center justify-center" 
                                    style={{ backgroundColor: `${CATEGORY_COLORS[status.category]}20` }}
                                  >
                                    <CategoryIcon 
                                      className="w-4 h-4" 
                                      style={{ color: CATEGORY_COLORS[status.category] }} 
                                    />
                                  </div>
                                );
                              })()}
                              <span className="text-sm font-medium">{status.category}</span>
                              <Badge variant="outline" className="text-xs capitalize">{status.period}</Badge>
                            </div>
                            <div className="text-right text-sm">
                              <span className="text-muted-foreground">
                                ${status.spent.toFixed(2)} / ${status.budget_amount.toFixed(2)}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">({status.days_remaining}d left)</span>
                            </div>
                          </div>
                          <Progress 
                            value={Math.min(status.percent_used, 100)} 
                            className={`h-2 ${
                              status.percent_used >= 100 
                                ? '[&>div]:bg-red-500' 
                                : status.percent_used >= alertThreshold 
                                  ? '[&>div]:bg-amber-500' 
                                  : ''
                            }`} 
                          />
                          {status.daily_budget_remaining > 0 && (
                            <p className="text-xs text-muted-foreground">
                              ~${status.daily_budget_remaining.toFixed(2)}/day remaining
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Transactions */}
              {cardTransactions.length > 0 && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-lg">Recent Transactions</CardTitle>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-2" />Export
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => handleExportCSV(selectedCard.id)}>
                          <Download className="w-4 h-4 mr-2" />CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExportPDF(selectedCard.id)}>
                          <FileText className="w-4 h-4 mr-2" />Report
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-2">
                        {cardTransactions.map((tx) => {
                          const Icon = getCategoryIcon(tx.category);
                          return (
                            <div 
                              key={tx.id} 
                              className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                            >
                              <div 
                                className="w-10 h-10 rounded-xl flex items-center justify-center" 
                                style={{ backgroundColor: `${CATEGORY_COLORS[tx.category]}20` }}
                              >
                                <Icon style={{ color: CATEGORY_COLORS[tx.category] }} className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{tx.merchant}</p>
                                <p className="text-xs text-muted-foreground">{tx.category}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-mono font-semibold text-red-500">-${tx.amount.toFixed(2)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(tx.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </motion.div>
      )}
    </div>
  );
};

export default VirtualCardsTab;
